import type { ClientMessage, PeerView, RoomDoc, ServerMessage } from '../../server/protocol';

/** Verbindungszustand aus Nutzersicht. */
export type CollabStatus = 'offline' | 'connecting' | 'online' | 'error';

export interface CollabClientHandlers {
  onWelcome(msg: Extract<ServerMessage, { t: 'welcome' }>): void;
  onPeers(msg: Extract<ServerMessage, { t: 'peers' }>): void;
  onRemote(msg: Extract<ServerMessage, { t: 'remote' }>): void;
  onReset(msg: Extract<ServerMessage, { t: 'reset' }>): void;
  onPresence(msg: Extract<ServerMessage, { t: 'presence' }>): void;
  onView(msg: Extract<ServerMessage, { t: 'view' }>): void;
  onFollowInvite(msg: Extract<ServerMessage, { t: 'follow-invite' }>): void;
  onFollowInviteResponse(msg: Extract<ServerMessage, { t: 'follow-invite-response' }>): void;
  onStatus(status: CollabStatus, message?: string): void;
}

const MAX_RETRY_MS = 10_000;

/**
 * WebSocket-Transport für die Kollaboration: verbindet sich mit `/ws`, joint einen
 * Raum, sendet Nachrichten und verbindet sich bei Verbindungsabbruch automatisch
 * neu (exponentielles Backoff). Fire-and-forget-Nachrichten (Presence) gehen nur
 * bei offener Verbindung verloren; Ops werden bewusst **nicht** gepuffert — der
 * Sync-Engine zählt Differenzen gegenüber dem Server-Stand und holt beim
 * Reconnect alles nach.
 */
export class CollabClient {
  private ws: WebSocket | null = null;
  private joinPayload: Extract<ClientMessage, { t: 'join' }> | null = null;
  private closedByUs = false;
  private stopReconnect = false;
  private attempts = 0;
  private retryTimer: number | null = null;
  private statusValue: CollabStatus = 'offline';

  constructor(private handlers: CollabClientHandlers) {}

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get status(): CollabStatus {
    return this.statusValue;
  }

  connect(roomId: string, name: string, doc?: RoomDoc): void {
    this.disconnect();
    this.closedByUs = false;
    this.stopReconnect = false;
    this.attempts = 0;
    this.joinPayload = doc ? { t: 'join', roomId, name, doc } : { t: 'join', roomId, name };
    this.open();
  }

  sendOps(ops: Extract<ClientMessage, { t: 'ops' }>['ops'], meta?: Extract<ClientMessage, { t: 'ops' }>['meta']): void {
    this.sendWhenOpen({ t: 'ops', ops, meta });
  }

  sendState(doc: RoomDoc): void {
    this.sendWhenOpen({ t: 'state', doc });
  }

  sendPresence(cursor: [number, number, number] | null): void {
    this.sendWhenOpen({ t: 'presence', cursor });
  }

  /** Eigene Kamera-Ansicht melden (Folgen-Modus); null = keine Ansicht bekannt. */
  sendView(view: PeerView | null): void {
    this.sendWhenOpen({ t: 'view', view });
  }

  /** Einen Teilnehmer einladen, der eigenen Ansicht zu folgen. */
  sendFollowInvite(to: string): void {
    this.sendWhenOpen({ t: 'follow-invite', to });
  }

  /** Auf eine Folge-Einladung antworten (accepted = true: „X" folgt nun). */
  sendFollowInviteResponse(to: string, accepted: boolean): void {
    this.sendWhenOpen({ t: 'follow-invite-response', to, accepted });
  }

  rename(name: string): void {
    this.sendWhenOpen({ t: 'rename', name });
  }

  /** Verbindung sauber beenden (kein Reconnect). */
  disconnect(): void {
    this.closedByUs = true;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.connected) {
      try {
        this.ws!.send(JSON.stringify({ t: 'leave' } satisfies ClientMessage));
      } catch {
        // Verbindung ist eh weg
      }
    }
    this.ws?.close();
    this.ws = null;
    this.setStatus('offline');
  }

  // ---------- Internas ----------

  private open(): void {
    this.setStatus('connecting');
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;

    ws.onopen = () => {
      this.attempts = 0;
      if (this.joinPayload) ws.send(JSON.stringify(this.joinPayload));
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(ev.data)) as ServerMessage;
      } catch {
        return;
      }
      switch (msg.t) {
        case 'welcome':
          this.setStatus('online');
          this.handlers.onWelcome(msg);
          break;
        case 'peers':
          this.handlers.onPeers(msg);
          break;
        case 'remote':
          this.handlers.onRemote(msg);
          break;
        case 'reset':
          this.handlers.onReset(msg);
          break;
        case 'presence':
          this.handlers.onPresence(msg);
          break;
        case 'view':
          this.handlers.onView(msg);
          break;
        case 'follow-invite':
          this.handlers.onFollowInvite(msg);
          break;
        case 'follow-invite-response':
          this.handlers.onFollowInviteResponse(msg);
          break;
        case 'error':
          // Schwerer Fehler (z. B. Raum voll) → nicht endlos neu verbinden
          this.stopReconnect = true;
          this.disconnect();
          this.handlers.onStatus('error', msg.message);
          break;
      }
    };

    ws.onclose = () => {
      this.ws = null;
      if (this.closedByUs || this.stopReconnect) return;
      // Nach mehreren Fehlversuchen klar sagen, was los ist — sonst wirkt die App
      // einfach „offline“, obwohl der Nutzer auf die Synchronisation wartet.
      const hint =
        this.attempts >= 3 ? 'Kollaborations-Server nicht erreichbar — verbinde erneut …' : undefined;
      this.setStatus('offline', hint);
      const delay = Math.min(MAX_RETRY_MS, 800 * 2 ** this.attempts++);
      this.retryTimer = window.setTimeout(() => this.open(), delay);
    };

    ws.onerror = () => {
      // `close` folgt und übernimmt die Wiederverbindungs-Logik
    };
  }

  private sendWhenOpen(msg: ClientMessage): void {
    if (!this.connected) return;
    try {
      this.ws!.send(JSON.stringify(msg));
    } catch {
      // Verbindungsabbruch mitten im Senden — der Reconnect-Fluss regelt den Rest
    }
  }

  private setStatus(status: CollabStatus, message?: string): void {
    if (this.statusValue === status && !message) return;
    this.statusValue = status;
    this.handlers.onStatus(status, message);
  }
}
